/**
 * @file CabinetLayout.tsx
 * @description Composant de mise en page pour le cabinet, incluant les tiroirs de navigation et le rendu des sous-routes.
 */
import React from 'react';
import './CabinetLayout.css';
import { Outlet, useLocation } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import MainMenu from '../components/MainMenu/MainMenu';
import DossierDrawer from '../components/DossierDrawer/DossierDrawer';
import ClientMenu from '../components/ClientMenu/ClientMenu';
import { useAuthStore } from '../store/auth/useAuthStore';
import { useCabinetBootstrap } from '../hooks/useCabinetBootstrap';
import { ReloadOutlined } from '@ant-design/icons';

const CabinetLayout: React.FC = () => {
  useCabinetBootstrap();

  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const userCabinetId = useAuthStore((state) => state.userCabinetId);
  const userCabinet = useAuthStore((state) => state.userCabinet);
  const cabinetBootstrapStatus = useAuthStore((state) => state.cabinetBootstrapStatus);
  const cabinetBootstrapError = useAuthStore((state) => state.cabinetBootstrapError);
  const retryCabinetBootstrap = useAuthStore((state) => state.retryCabinetBootstrap);

  const isCabinetBootstrapBlocked = Boolean(userCabinetId) && userCabinet?.id !== userCabinetId;

  const renderDrawer = () => {
    const dashboardPaths = [
      '/cabinet/parametre',
      '/cabinet/dossier-point-suspens',
      '/cabinet/parametre-general',
      '/cabinet/parametre-comptabilite/fiscal',
      '/cabinet/parametre-comptabilite',
      '/cabinet/parametre-comptabilite/journaux',
      '/cabinet/parametre-comptabilite/plan-comptable',
      '/cabinet/parametre-comptabilite/lettrage-auto',
      '/cabinet/parametre-comptabilite/politique-immobilisation',
      '/cabinet/parametre-comptabilite/analytique',
      '/cabinet/points-suspension',
    ];

    const mainMenuPaths = [
      '/cabinet',
      '/cabinet/assignation-intervenants',
      '/cabinet/recherche',
      '/cabinet/entreprise',
      '/cabinet/invitation-client',
    ];

    if (mainMenuPaths.includes(location.pathname)) {
      return <MainMenu />;
    }

    if (
      dashboardPaths.includes(location.pathname) ||
      location.pathname.startsWith('/cabinet/production-comptable') ||
      location.pathname.startsWith('/cabinet/cut-off') ||
      location.pathname.startsWith('/cabinet/emprunts') ||
      location.pathname.startsWith('/cabinet/comptes-courants') ||
      location.pathname.startsWith('/cabinet/production-comptable/grand-livre') ||
      location.pathname.startsWith('/cabinet/precompta/finconnect') ||
      location.pathname.startsWith('/cabinet/immobilisations')
    ) {
      return <DossierDrawer />;
    }

    if (['/cabinet/tableau-de-bord', '/cabinet/questionnaire'].includes(location.pathname)) {
      return user?.roles.includes('ROLE_COLLABORATEUR_CABINET') ? (
        <DossierDrawer />
      ) : (
        <ClientMenu />
      );
    }

    return null; // ou un fallback par défaut
  };

  if (isCabinetBootstrapBlocked && cabinetBootstrapStatus === 'error') {
    return (
      <div className="page">
        <Result
          status="error"
          title="Informations du cabinet indisponibles"
          subTitle={
            cabinetBootstrapError ||
            "Le cabinet n'a pas pu être chargé. Réessayez avant d'utiliser ces actions."
          }
          extra={
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={retryCabinetBootstrap}
              aria-label="Réessayer"
            >
              Réessayer
            </Button>
          }
        />
      </div>
    );
  }

  if (isCabinetBootstrapBlocked) {
    return (
      <div
        className="page"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          paddingTop: 120,
        }}
      >
        {/* Prevent rendering cabinet routes while actions would still fail with a missing cabinet id. */}
        <Spin size="large" />
        <div>Chargement des informations du cabinet...</div>
      </div>
    );
  }

  return (
    <>
      <div className="drawer">{renderDrawer()}</div>
      <div className="page">
        {/* Votre mise en page cabinet ici */}
        <Outlet /> {/* Rendu des sous-routes */}
      </div>
    </>
  );
};

export default CabinetLayout;
